<template>
<validation-observer ref="clientGroupForm" #default="{invalid}">
  <div>
    <h4 class="settings-card-title">
      {{ title }}
    </h4>
    <b-card no-body class="px-2 py-2">
      <h6 class="settings-section-title">Access and Availability</h6>
      <b-form-group label-cols-sm="4" label-cols-lg="4" content-col-sm content-cols-lg-7>
        <validation-provider #default="{ errors }" name="Enable Maintenance Mode" rules="">
          <b-form-checkbox id="enable_maintenance_mode" v-model="settingsData.enable_maintenance_mode" name="enable_maintenance_mode">
            Enable Maintenance Mode
          </b-form-checkbox>
          <small class="d-block text-muted">Puts the system into maintenance mode while maintenance is carried out.</small>
          <small class="text-danger">{{ errors[0] }}</small>
        </validation-provider>
      </b-form-group>

      <b-form-group label-cols-sm="4" label-cols-lg="4" content-col-sm content-cols-lg-7>
        <validation-provider #default="{ errors }" name="Disable New Sign-Ups" rules="">
          <b-form-checkbox id="disable_new_signups" v-model="settingsData.disable_new_signups" name="disable_new_signups">
            Disable New Sign-Ups
          </b-form-checkbox>
          <small class="d-block text-muted">Prevents new users from registering. Existing users can still sign in.</small>
          <small class="text-danger">{{ errors[0] }}</small>
        </validation-provider>
      </b-form-group>

      <h6 class="settings-section-title">Data and Users</h6>
      <b-form-group label-cols-sm="4" label-cols-lg="4" content-col-sm content-cols-lg-7>
        <validation-provider #default="{ errors }" name="Enable Migrations" rules="">
          <b-form-checkbox id="enable_migrations" v-model="settingsData.enable_migrations" name="enable_migrations">
            Enable Migrations
          </b-form-checkbox>
          <small class="d-block text-muted">Allows data migration and bulk import tools to run. Turn off when not in use.</small>
          <small class="text-danger">{{ errors[0] }}</small>
        </validation-provider>
      </b-form-group>

      <b-form-group label-cols-sm="4" label-cols-lg="4" content-col-sm content-cols-lg-7>
        <validation-provider #default="{ errors }" name="Send Imported Users Password Reset Email" rules="">
          <b-form-checkbox id="send_imported_users_password_reset_email" v-model="settingsData.send_imported_users_password_reset_email" name="send_imported_users_password_reset_email">
            Send Imported Users Password Reset Email
          </b-form-checkbox>
          <small class="d-block text-muted">Emails a password reset link to each user created through a bulk import so they can set their own password.</small>
          <small class="text-danger">{{ errors[0] }}</small>
        </validation-provider>
      </b-form-group>

      <h6 class="settings-section-title">Environment</h6>
      <b-form-group label-cols-sm="4" label-cols-lg="4" content-col-sm content-cols-lg-7>
        <validation-provider #default="{ errors }" name="Sandbox Environment" rules="">
          <b-form-checkbox id="sandbox_mode" v-model="settingsData.sandbox_mode" name="sandbox_mode">
            Sandbox Environment
          </b-form-checkbox>
          <small class="d-block text-muted">Shows a banner and a "[SANDBOX]" tab title to distinguish this deployment from live.</small>
          <small class="text-danger">{{ errors[0] }}</small>
        </validation-provider>
      </b-form-group>

      <b-form-group
        v-if="settingsData.sandbox_mode"
        label="Sandbox Banner Message"
        label-for="sandbox_banner_message"
        label-cols-sm="4"
        label-cols-lg="4"
        content-col-sm
        content-cols-lg-7
        description="Shown in the banner at the top of every page (max 200 characters)."
      >
        <validation-provider #default="{ errors }" name="Sandbox Banner Message" rules="max:200">
          <b-form-input id="sandbox_banner_message" v-model="settingsData.sandbox_banner_message" maxlength="200" />
          <small class="text-danger">{{ errors[0] }}</small>
        </validation-provider>
      </b-form-group>

      <div>
        <b-button variant="primary" size="lg" :disabled="invalid" @click="$emit('update', { general_settings: settingsData })">
          Apply
        </b-button>
      </div>
    </b-card>
  </div>
</validation-observer>
</template>

<script>
import {
  ValidationProvider,
  ValidationObserver
} from 'vee-validate'
import {
  heightTransition
} from '@core/mixins/ui/transition'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'

import {
  BOverlay,
  BRow,
  BCol,
  BCard,
  BCardBody,
  BButton,
  BCardText,
  BFormTextarea,
  BFormCheckbox,
  BFormRadio,
  BFormGroup,
  BPopover,
  VBToggle,
  BFormSpinbutton,
  BFormFile,
  BForm,
  BFormInput,
  BLink,
  BInputGroup,
  VBTooltip,
} from 'bootstrap-vue'

const DEFAULT_GENERAL_SETTINGS = {
  enable_maintenance_mode: false,
  disable_new_signups: false,
  enable_migrations: false,
  send_imported_users_password_reset_email: false,
  sandbox_mode: false,
  sandbox_banner_message: ''
}

export default {
  components: {
    BOverlay,
    BRow,
    BCol,
    BCard,
    BCardBody,
    BButton,
    BCardText,
    BPopover,
    vSelect,
    BFormTextarea,
    BFormCheckbox,
    BFormRadio,
    BFormGroup,
    BFormSpinbutton,
    BFormFile,
    BForm,
    BFormInput,
    BLink,
    BInputGroup,

    // validations
    ValidationProvider,
    ValidationObserver
  },
  directives: {
    Ripple,
    'b-toggle': VBToggle,
    'b-tooltip': VBTooltip
  },
  mixins: [heightTransition],
  props: {
    title: {
      type: String,
      default: 'General Settings'
    },
    generalSettings: {
      type: Object,
      default: () => ({}),
      required: true
    },
  },
  data() {
    return {
      settingsData: { ...DEFAULT_GENERAL_SETTINGS }
    }
  },
  watch: {
    generalSettings: {
      handler(setting) {
        // Fill in any flag missing from older records so the update payload is always complete.
        this.settingsData = { ...DEFAULT_GENERAL_SETTINGS, ...setting }
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss" scoped>
.settings-card-title {
  font-weight: 400;
  font-size: 20px;
  line-height: 160%;

  color: #888B97;
}

.settings-section-title {
  font-weight: 600;
  color: #5e5873;
  margin: 0.5rem 0 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #ebe9f1;
}

input {
  width: 150px;
}
</style>
